import { useEffect, useRef, useState } from "react"

/**
 * Returns `value` immediately while it's > 0, but when it drops to 0 the
 * previous non-zero value is held for `holdMs` before the display becomes 0.
 *
 * Use this to prevent the unread badge from snapping to 0 the moment we
 * ack — so the user gets at least `holdMs` of visual confirmation that
 * the read happened. New non-zero values (e.g. a message arriving during
 * the hold window) update the display immediately and reset any pending
 * hide.
 */
export function useStickyNonZero(value: number, holdMs = 5000): number {
	const [display, setDisplay] = useState(value)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		if (value > 0) {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
				timerRef.current = null
			}
			setDisplay(value)
			return
		}
		if (display === 0) return
		if (timerRef.current) return
		timerRef.current = setTimeout(() => {
			setDisplay(0)
			timerRef.current = null
		}, holdMs)
	}, [value, display, holdMs])

	useEffect(
		() => () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		},
		[],
	)

	return display
}
